import React, { useState, useEffect } from 'react';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { toastedSuccess } from '../config/toast';

const visibilityInfo = {
     Public: {
          icon: '🌍',
          msg: 'Everyone can see this post. It will appear on Explore and be searchable.',
     },
     Private: {
          icon: '🔒',
          msg: 'Only you can see this post. It will be hidden from Explore and other users.',
     },
};

const EditProblemOffcanvas = ({ show, handleClose, problemData, onUpdated }) => {
     const [title, setTitle] = useState('');
     const [problem, setProblem] = useState('');
     const [content, setContent] = useState('');
     const [visibility, setVisibility] = useState('Public');
     const [saving, setSaving] = useState(false);
     const [error, setError] = useState('');

     useEffect(() => {
          if (problemData) {
               setTitle(problemData.title || '');
               setProblem(problemData.problem || '');
               setContent(problemData.content || '');
               setVisibility(problemData.visibility || 'Public');
          }
     }, [problemData]);

     const handleSave = async () => {
          if (!title.trim() || !problem.trim()) {
               setError('Title and Problem are required.');
               return;
          }
          setSaving(true);
          setError('');
          try {
               const ref = doc(db, 'problems', problemData.id);
               await updateDoc(ref, {
                    title,
                    problem,
                    content,
                    visibility,
                    isEdited: 'edited',
                    editedAt: new Date(),
               });
               onUpdated({ title, problem, content, visibility });
               toastedSuccess('Changes saved successfully!');
               handleClose();
          } catch (err) {
               console.error('Edit error:', err);
               setError('Failed to save. Try again.');
          } finally {
               setSaving(false);
          }
     };

     return (
          <Modal
               open={show}
               onClose={handleClose}
               center
               classNames={{ modal: 'edit-offcanvas' }}
               onAnimationEnd={() => show && toastedSuccess('Edit mode opened!')}
          >
               <div className="offcanvas-header">
                    <h5 className="offcanvas-title">Edit Post</h5>
               </div>

               <div className="offcanvas-body">
                    {error && <div className="error-msg">{error}</div>}

                    <div className="form-group">
                         <label>Title <span className="char-count">{title.length}/150</span></label>
                         <input
                              type="text"
                              value={title}
                              onChange={e => setTitle(e.target.value.slice(0, 150))}
                              placeholder="Enter title..."
                              maxLength={150}
                         />
                    </div>

                    <div className="form-group">
                         <label>Problem <span className="char-count">{problem.length}/800</span></label>
                         <textarea
                              value={problem}
                              onChange={e => setProblem(e.target.value.slice(0, 800))}
                              placeholder="Describe the problem..."
                              rows={4}
                              maxLength={800}
                         />
                    </div>

                    <div className="form-group">
                         <label>Content</label>
                         <ReactQuill
                              theme="snow"
                              value={content}
                              onChange={setContent}
                              placeholder="Add detailed content..."
                         />
                    </div>

                    <div className="form-group">
                         <label>Visibility</label>
                         <div className="visibility-toggle">
                              {['Public', 'Private'].map(opt => (
                                   <button
                                        key={opt}
                                        type="button"
                                        className={`vis-btn ${visibility === opt ? 'active' : ''}`}
                                        onClick={() => setVisibility(opt)}
                                   >
                                        {visibilityInfo[opt].icon} {opt}
                                   </button>
                              ))}
                         </div>
                         <div className={`vis-info ${visibility === 'Private' ? 'private' : 'public'}`}>
                              {visibilityInfo[visibility].msg}
                         </div>
                    </div>
               </div>

               <div className="offcanvas-footer">
                    <button className="btn-cancel" onClick={handleClose}>Cancel</button>
                    <button className="btn-save" onClick={handleSave} disabled={saving}>
                         {saving ? 'Saving...' : 'Save Changes'}
                    </button>
               </div>
          </Modal>
     );
};

export default EditProblemOffcanvas;
